import React, { useState } from "react";
import { Plus, X, Wand2, ChevronDown } from "lucide-react";
import { metadataGroupsAPI } from "../../services/api";
import Modal from "../ui/Modal";

const EMPTY_ATTR = { name: "", value: "", auto_generate: false, generation_prompt: "" };

// Minimal inline create-group modal
function CreateGroupModal({ onSave, onClose, loading }) {
  const [name, setName] = useState("");
  const [attrs, setAttrs] = useState([{ ...EMPTY_ATTR }]);

  const canSave = name.trim() && attrs.length > 0 && attrs.every((a) =>
    a.name.trim() && (a.auto_generate ? a.generation_prompt.trim() : a.value.trim())
  );

  const update = (i, patch) => setAttrs((prev) => prev.map((a, idx) => (idx === i ? { ...a, ...patch } : a)));
  const remove = (i) => setAttrs((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => setAttrs((prev) => [...prev, { ...EMPTY_ATTR }]);

  return (
    <Modal
      title="New Attribute Group"
      onClose={onClose}
      maxWidth="max-w-lg"
      footer={
        <>
          <button type="button" onClick={onClose} className="btn btn-secondary">Cancel</button>
          <button
            type="button"
            onClick={() => canSave && onSave({ name: name.trim(), items: attrs })}
            disabled={!canSave || loading}
            className="btn btn-primary"
          >
            {loading ? "Saving…" : "Create & Select"}
          </button>
        </>
      }
    >
      <div>
        <label className="form-label">Group Name <span className="text-red-400">*</span></label>
        <input
          autoFocus
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="e.g. Premium Customer"
          className="form-input"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="form-label mb-0">Attributes <span className="text-red-400">*</span></label>
          <button type="button" onClick={add} className="btn btn-sm btn-secondary">
            <Plus className="h-3 w-3" /> Add row
          </button>
        </div>
        <div className="space-y-2 max-h-52 overflow-y-auto">
          {attrs.map((attr, i) => (
            <div key={i} className="flex items-center gap-2">
              <input
                type="text"
                placeholder="Name"
                value={attr.name}
                onChange={(e) => update(i, { name: e.target.value })}
                className="form-input w-1/3"
              />
              <input
                type="text"
                placeholder={attr.auto_generate ? "Generation prompt" : "Value"}
                value={attr.auto_generate ? attr.generation_prompt : attr.value}
                onChange={(e) =>
                  update(i, attr.auto_generate ? { generation_prompt: e.target.value } : { value: e.target.value })
                }
                className="form-input flex-1"
              />
              <label className="inline-flex items-center gap-1 cursor-pointer flex-shrink-0" title="AI auto-generate">
                <input
                  type="checkbox"
                  checked={attr.auto_generate}
                  onChange={(e) => update(i, { auto_generate: e.target.checked, value: "", generation_prompt: "" })}
                  className="h-3.5 w-3.5 rounded text-primary border-input"
                />
                <Wand2 className="h-3.5 w-3.5 text-violet-500" />
              </label>
              <button type="button" onClick={() => remove(i)} className="btn-icon btn-ghost flex-shrink-0">
                <X className="h-3.5 w-3.5 text-red-400" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

/**
 * Picks one or more MetadataGroups to attach to a scenario.
 *
 * Props:
 *   groups           – all available groups (array)
 *   selectedIds      – currently selected group IDs (array of int)
 *   onChange         – (newIds: int[]) => void
 *   currentWorkspace – { id }
 *   onGroupCreated   – () => void  refresh parent's group list after creation
 */
export default function AttributeGroupPicker({
  groups = [],
  selectedIds = [],
  onChange,
  currentWorkspace,
  onGroupCreated,
}) {
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);

  const selectedGroups = groups.filter((g) => selectedIds.includes(g.id));
  const unselectedGroups = groups.filter((g) => !selectedIds.includes(g.id));

  const handleSelect = (e) => {
    const id = parseInt(e.target.value);
    if (!id || selectedIds.includes(id)) return;
    onChange([...selectedIds, id]);
    e.target.value = "";
  };

  const handleRemove = (id) => {
    onChange(selectedIds.filter((i) => i !== id));
  };

  const handleCreate = async (data) => {
    try {
      setCreating(true);
      const created = await metadataGroupsAPI.createGroup(data, currentWorkspace.id);
      onGroupCreated?.();
      onChange([...selectedIds, created.id]);
      setShowCreate(false);
    } catch (e) {
      console.error("Failed to create group", e);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="space-y-3">
      {/* Selected group chips */}
      {selectedGroups.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedGroups.map((group) => (
            <div
              key={group.id}
              className="inline-flex items-center gap-2 px-3 py-1.5 bg-primary/5 border border-primary/20 rounded-lg"
            >
              <div>
                <span className="text-sm font-medium text-primary">{group.name}</span>
                <span className="ml-1.5 text-xs text-primary/60">
                  {(group.items || []).length} attr{(group.items || []).length !== 1 ? "s" : ""}
                </span>
              </div>
              <button
                type="button"
                onClick={() => handleRemove(group.id)}
                className="p-0.5 rounded hover:bg-primary/10"
              >
                <X className="h-3.5 w-3.5 text-primary/60" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Picker row */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <select
            onChange={handleSelect}
            defaultValue=""
            className="form-select pr-8 appearance-none"
            disabled={unselectedGroups.length === 0}
          >
            <option value="">
              {unselectedGroups.length === 0
                ? groups.length === 0
                  ? "No groups yet — create one"
                  : "All groups selected"
                : "Select an attribute group…"}
            </option>
            {unselectedGroups.map((g) => (
              <option key={g.id} value={g.id}>
                {g.name} ({(g.items || []).length} attributes)
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        </div>
        <button
          type="button"
          onClick={() => setShowCreate(true)}
          className="btn btn-secondary flex-shrink-0"
        >
          <Plus className="h-4 w-4" />
          New group
        </button>
      </div>

      {/* Preview of selected attributes */}
      {selectedGroups.length > 0 && (
        <div className="flex flex-wrap gap-1.5 pt-1">
          {selectedGroups.flatMap((g) =>
            (g.items || []).map((attr, i) => (
              <span
                key={`${g.id}-${i}`}
                className={`badge ${attr.auto_generate ? "badge-violet" : "badge-gray"}`}
              >
                {attr.auto_generate && <Wand2 className="h-2.5 w-2.5" />}
                {attr.name}{!attr.auto_generate && attr.value ? `=${attr.value}` : ""}
              </span>
            ))
          )}
        </div>
      )}

      {showCreate && (
        <CreateGroupModal
          onSave={handleCreate}
          onClose={() => setShowCreate(false)}
          loading={creating}
        />
      )}
    </div>
  );
}
